import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { vacationCompensation, type VacationCompensationItem } from '@/api/vacation';
import { refs } from '@/api/refs';
import { esvTipDoc, trimNum } from '@/shared/taxTips';
import InfoQ from '@/shared/components/InfoQ';
import ProjectSelect from '@/components/ProjectSelect';
import DocAccountsBlock from '@/shared/components/DocAccountsBlock';
import { useConfirm } from '@/components/ConfirmProvider';

const fmt2 = (v: string | number | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { detail?: string } }; message?: string };
  return err?.response?.data?.detail ?? err?.message ?? 'Помилка';
}

export default function VacationCompensationEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { id } = useParams();
  const [doc, setDoc] = useState<VacationCompensationItem | null>(null);
  const [days, setDays] = useState('');
  const [avg, setAvg] = useState('');
  // Проект / напрям (упр. облік) — потрапляє у субконто-3 проводок при проведенні.
  const [projectId, setProjectId] = useState('');
  const [docTab, setDocTab] = useState<'calc' | 'accounts'>('calc');
  const [expCode, setExpCode] = useState('92');
  const [error, setError] = useState<string | null>(null);

  const docQ = useQuery({
    queryKey: ['vacation-compensation', id],
    queryFn: () => vacationCompensation.get(id!),
    enabled: !!id,
  });
  // Iм'я працівника (через org із документа).
  const empQ = useQuery({
    queryKey: ['employees', doc?.organization_id],
    queryFn: () => refs.employees(doc!.organization_id, {}),
    enabled: !!doc?.organization_id,
  });
  const empName = empQ.data?.find((e) => e.id === doc?.employee_id)?.name ?? '—';

  useEffect(() => {
    if (docQ.data) {
      setDoc(docQ.data);
      setDays(docQ.data.compensation_days);
      setAvg(docQ.data.average_daily_wage);
      setProjectId(docQ.data.project_id ?? '');
      setExpCode((docQ.data as { expense_account_code?: string }).expense_account_code ?? '92');
    }
  }, [docQ.data]);

  const isPosted = doc?.posted ?? false;

  const recalcMut = useMutation({
    mutationFn: () => vacationCompensation.update(id!, days, avg, projectId || null),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  // Зміна проекту зберігається одразу (дні/середня — поточні, без збиття).
  const projMut = useMutation({
    mutationFn: (pid: string) => vacationCompensation.update(id!, days, avg, pid || null),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  // Зміна рахунку витрат документа — зберігається одразу.
  const expMut = useMutation({
    mutationFn: (code: string) =>
      vacationCompensation.update(id!, days, avg, undefined, code || null),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  const postMut = useMutation({
    mutationFn: () => vacationCompensation.post(id!),
    onSuccess: (r) => {
      setDoc(r); setError(null);
      qc.invalidateQueries({ queryKey: ['vacation-compensations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpostMut = useMutation({
    mutationFn: () => vacationCompensation.unpost(id!),
    onSuccess: (r) => { setDoc(r); setError(null); },
    onError: (e) => setError(extractError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: () => vacationCompensation.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vacation-compensations'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate('/payrolls');
    },
    onError: (e) => setError(extractError(e)),
  });

  if (docQ.isLoading || !doc) return <div className="card">Завантаження…</div>;

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Нарахування (компенсація відпустки) № {doc.document_number}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          {!isPosted && (
            <button className="primary" disabled={postMut.isPending}
              onClick={() => postMut.mutate()}>▶ Провести</button>
          )}
          {isPosted && (
            <button disabled={unpostMut.isPending}
              onClick={() => unpostMut.mutate()}>◀ Розпровести</button>
          )}
          <button
            style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}
            disabled={deleteMut.isPending}
            title="Видаляє лише нарахування компенсації (наказ про звільнення лишається)"
            onClick={() => {
              confirm('Видалити нарахування компенсації? (наказ про звільнення лишиться)')
                .then((ok) => { if (ok) deleteMut.mutate(); });
            }}>🗑 Видалити</button>
          <button onClick={() => navigate('/payrolls')}>← До списку</button>
          <button onClick={() => navigate('/payrolls')}>Закрити</button>
        </div>
      </div>

      {isPosted && (
        <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6,
          padding: '0.4rem 0.6rem', marginBottom: '0.75rem', color: '#166534' }}>
          ✓ Компенсацію проведено. Для редагування — спершу «Розпровести».
        </div>
      )}

      {/* Шапка (read-only): працівник + наказ + період. Створено з наказу звільнення. */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.85rem',
        color: '#475569', marginBottom: '0.75rem' }}>
        <span><strong>Працівник:</strong> {empName}</span>
        <span><strong>Дата звільнення:</strong> {new Date(doc.dismiss_date).toLocaleDateString('uk-UA')}</span>
        <span><strong>Розрахунковий період:</strong> {doc.calc_period_start} — {doc.calc_period_end}</span>
        <a href={`/dismissal-orders/${doc.dismissal_order_id}`}
          onClick={(e) => { e.preventDefault(); navigate(`/dismissal-orders/${doc.dismissal_order_id}`); }}
          style={{ color: '#2563eb', cursor: 'pointer' }}>Наказ звільнення →</a>
      </div>

      {/* Редаговані поля (тільки DRAFT). */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <div className="field" style={{ width: 160 }}>
          <label>Днів до компенсації</label>
          <input type="number" step="0.01" value={days} disabled={isPosted}
            onChange={(e) => setDays(e.target.value)} />
        </div>
        <div className="field" style={{ width: 160 }}>
          <label>Середньоденна</label>
          <input type="number" step="0.01" value={avg} disabled={isPosted}
            onChange={(e) => setAvg(e.target.value)} />
        </div>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Проект / напрям</label>
          <ProjectSelect
            value={projectId}
            organizationId={doc.organization_id}
            onChange={(v) => { setProjectId(v ?? ''); projMut.mutate(v ?? ''); }}
            disabled={isPosted} />
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      {/* Закладки: розрахунок ↔ рахунки обліку (рішення власника 2026-07-07). */}
      <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6,
        overflow: 'hidden', width: 'fit-content', marginBottom: '0.5rem' }}>
        {([['calc', 'Розрахунок'], ['accounts', 'Рахунки обліку']] as const).map(([t, l]) => (
          <button key={t} onClick={() => setDocTab(t)}
            style={{ padding: '0.3rem 0.9rem', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
              background: docTab === t ? 'var(--primary)' : 'transparent',
              color: docTab === t ? '#fff' : 'var(--text-secondary)' }}>
            {l}
          </button>
        ))}
      </div>
      {docTab === 'accounts' && (
        <DocAccountsBlock
          organizationId={doc.organization_id}
          employeeIds={[doc.employee_id]}
          paymentKind="COMPENSATION"
          headerCodes={{ expense: expCode, payroll: '661', pdfo: '6411', wz: '6415', esv: '651' }}
          expenseCode={expCode}
          onExpenseChange={(v) => { setExpCode(v); if (v) expMut.mutate(v); }}
          disabled={isPosted}
          nameOf={() => empName}
        />
      )}

      {docTab === 'calc' && (<>
      {/* Кнопка «Розрахувати» справа над таблицею (дизайн = Нарахування ЗП). */}
      {!isPosted && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.4rem' }}>
          <button className="primary" disabled={recalcMut.isPending || !days || !avg}
            onClick={() => recalcMut.mutate()}
            title="Перерахунок брутто/ПДФО/ВЗ/ЄСВ за дні × середню (інвалід 8.41%, ліміт ЄСВ).">
            Розрахувати
          </button>
        </div>
      )}

      {/* Широка таблична частина — суми нарахування (дизайн = Нарахування ЗП). */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'right' }}>
              <th style={{ textAlign: 'left', padding: '0.2rem 0.3rem' }}>Працівник</th>
              <th style={{ padding: '0.2rem 0.3rem', width: 70 }}>Днів</th>
              <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>Середня</th>
              <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>Брутто</th>
              <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ПДФО</th>
              <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ВЗ</th>
              <th style={{ padding: '0.2rem 0.3rem', width: 95 }}>ЄСВ</th>
              <th style={{ padding: '0.2rem 0.3rem', width: 100 }}>До виплати</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.15rem 0.3rem' }}>{empName}</td>
              <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{fmt2(doc.compensation_days)}</td>
              <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right' }}>{fmt2(doc.average_daily_wage)}</td>
              <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right', fontWeight: 700 }}>{fmt2(doc.compensation_gross_amount)}</td>
              <td style={{ padding: '0.15rem 0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                    value={trimNum(doc.pdfo_amount)}
                    style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                  <InfoQ title={`ПДФО — ${trimNum(doc.pdfo_rate ?? '18')}% від брутто.`} />
                </div>
              </td>
              <td style={{ padding: '0.15rem 0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                    value={trimNum(doc.wz_amount)}
                    style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                  <InfoQ title={`Військовий збір — ${trimNum(doc.wz_rate ?? '5')}% від брутто.`} />
                </div>
              </td>
              <td style={{ padding: '0.15rem 0.3rem' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="number" className="no-spin pe-cell" readOnly tabIndex={-1}
                    value={trimNum(doc.esv_amount)}
                    style={{ textAlign: 'right', width: '100%', background: '#f1f5f9', color: '#475569' }} />
                  <InfoQ title={esvTipDoc(doc.esv_amount, doc.esv_rate ?? '22')} />
                </div>
              </td>
              <td style={{ padding: '0.15rem 0.3rem', textAlign: 'right', fontWeight: 700 }}>{fmt2(doc.net_amount)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      </>)}
      <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.5rem' }}>
        Виплата компенсації — через «Виплата нарахувань» (борг видно у «Нарахування виплат → Борги»).
      </p>
    </div>
  );
}
